import { timezoneListDtoSchema, type TimezoneListDto } from '@lib/dto/venue.dto'
import type { SignupInput } from '@lib/input/signup.input'
import type { ConfirmEmailInput, VendorJoinRequestInput, VendorSignupInput, VendorSimilarInput } from '@lib/input/vendor-signup.input'
import { similarVendorListDtoSchema, type SimilarVendorListDto } from '@lib/dto/vendor-signup.dto'
import { z } from 'zod'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  invitationSessionDtoSchema,
  linkAccountDtoSchema,
  magicLinkSessionDtoSchema,
  switchableVenuesDtoSchema,
  type InvitationSessionDto,
  type LinkAccountDto,
  type MagicLinkSessionDto,
  type SwitchableVenuesDto,
} from '@lib/dto/auth.dto'
import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  loginResponseSchema,
  userSchema,
  type LoginInput,
  type LoginResponse,
  type User,
} from '@/schemas/auth.schema'
import type { IAuthService } from '@/types/AuthService.types'
import type {
  AcceptInvitationInput,
  ChangePasswordInput,
  LinkAccountInput,
  MagicLinkSignInInput,
  RequestMagicLinkInput,
  RequestPasswordResetInput,
  ResetPasswordInput,
  UpdateProfileInput,
} from '@lib/input/user.input'

/**
 * Service for authentication endpoints. Every method validates its response
 * through the `api` client's schema boundary, so the returned values are typed
 * by the schemas, not by unchecked assertions.
 */
class AuthService implements IAuthService {
  /**
   * Log in with email and password.
   * @param input Login credentials.
   * @returns The access token and the signed-in user.
   */
  async login(input: LoginInput): Promise<LoginResponse> {
    try {
      return await api.post('/auth/login', input, loginResponseSchema)
    } catch (error) {
      logRequestFailure('Login failed', {}, error)
      throw error
    }
  }

  /**
   * Fetch the currently authenticated user.
   * @returns The signed-in user.
   */
  async me(): Promise<User> {
    try {
      return await api.get('/auth/me', userSchema)
    } catch (error) {
      logRequestFailure('Failed to load current user', {}, error)
      throw error
    }
  }

  /**
   * Revoke a refresh token server-side (logout). Best-effort — a failure here
   * shouldn't block the client from clearing its local session.
   * @param refreshToken The refresh token to revoke.
   */
  async logout(refreshToken: string): Promise<void> {
    try {
      await api.post('/auth/logout', { refreshToken }, z.null())
    } catch (error) {
      logger.warn('Server-side logout failed; clearing local session anyway', {
        error: toErrorMessage(error),
      })
    }
  }

  /** The time zones a new company's first venue may use. Public, like the signup form. */
  async signupTimezones(): Promise<TimezoneListDto> {
    try {
      return await api.get('/auth/signup/timezones', timezoneListDtoSchema, { anonymous: true })
    } catch (error) {
      logRequestFailure('Failed to load time zones', {}, error)
      throw error
    }
  }

  async signup(input: SignupInput): Promise<LoginResponse> {
    try {
      return await api.post('/auth/signup', input, loginResponseSchema)
    } catch (error) {
      logRequestFailure('Signup failed', {}, error)
      throw error
    }
  }

  /** Sign a vendor company up (docs/public-site.spec.md, "Vendor signup"). */
  async vendorSignup(input: VendorSignupInput): Promise<LoginResponse> {
    try {
      return await api.post('/auth/signup/vendor', input, loginResponseSchema, { anonymous: true })
    } catch (error) {
      logRequestFailure('Vendor signup failed', {}, error)
      throw error
    }
  }

  /** Companies already here like the one being signed up: the "Is this you?" step. */
  async vendorSimilar(input: VendorSimilarInput): Promise<SimilarVendorListDto> {
    try {
      return await api.post('/auth/signup/vendor/similar', input, similarVendorListDtoSchema, { anonymous: true })
    } catch (error) {
      logRequestFailure('Failed to look for similar companies', {}, error)
      throw error
    }
  }

  /** "That's us": ask to join a company already here. */
  async vendorJoin(input: VendorJoinRequestInput): Promise<void> {
    try {
      await api.post('/auth/signup/vendor/join', input, z.null(), { anonymous: true })
    } catch (error) {
      logRequestFailure('Failed to ask to join a company', { vendorId: input.vendorId }, error)
      throw error
    }
  }

  /** Spend the emailed link that confirms a vendor's email. */
  async confirmEmail(input: ConfirmEmailInput): Promise<void> {
    try {
      await api.post('/auth/confirm-email', input, z.null(), { anonymous: true })
    } catch (error) {
      logRequestFailure('Failed to confirm an email', {}, error)
      throw error
    }
  }

  /** Send the confirmation link again, for the vendor signed in. */
  async resendEmailConfirmation(): Promise<void> {
    try {
      await api.post('/auth/confirm-email/resend', {}, z.null())
    } catch (error) {
      logRequestFailure('Failed to send the confirmation link again', {}, error)
      throw error
    }
  }

  /** Accept an invitation: set the first password and get a session back. */
  async acceptInvitation(input: AcceptInvitationInput): Promise<InvitationSessionDto> {
    try {
      return await api.post('/auth/accept-invitation', input, invitationSessionDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to accept invitation', {}, error)
      throw error
    }
  }

  /** Whose account an invitation or password-reset link is for, without using the link. */
  async linkAccount(input: LinkAccountInput): Promise<LinkAccountDto> {
    try {
      return await api.post('/auth/link-account', input, linkAccountDtoSchema)
    } catch (error) {
      // No token in the breadcrumb — it is a live credential until it is spent.
      logRequestFailure('Failed to look up whose link this is', {}, error)
      throw error
    }
  }

  /** Request a password-reset email. Resolves regardless (no account disclosure). */
  /**
   * Ask for a sign-in link. Resolves the same way whatever the address is —
   * the API will not say whether it belongs to anybody, and neither will this.
   */
  async requestMagicLink(input: RequestMagicLinkInput): Promise<void> {
    try {
      await api.post('/auth/request-magic-link', input, z.null())
    } catch (error) {
      // Deliberately not logging the address: it is about to be the subject of
      // an email nobody else should be able to confirm the existence of.
      logRequestFailure('Failed to request a sign-in link', {}, error)
      throw error
    }
  }

  async signInWithMagicLink(input: MagicLinkSignInInput): Promise<MagicLinkSessionDto> {
    try {
      return await api.post('/auth/magic-link', input, magicLinkSessionDtoSchema)
    } catch (error) {
      // No token in the breadcrumb — it is a live credential until it is spent.
      logRequestFailure('Failed to sign in with a link', {}, error)
      throw error
    }
  }

  async requestPasswordReset(input: RequestPasswordResetInput): Promise<void> {
    try {
      await api.post('/auth/request-password-reset', input, z.null())
    } catch (error) {
      logRequestFailure('Failed to request password reset', {}, error)
      throw error
    }
  }

  /** Complete a password reset with the emailed token and a new password. */
  async resetPassword(input: ResetPasswordInput): Promise<void> {
    try {
      await api.post('/auth/reset-password', input, z.null())
    } catch (error) {
      logRequestFailure('Failed to reset password', {}, error)
      throw error
    }
  }

  /** Update the signed-in user's own name and/or avatar. */
  async updateProfile(input: UpdateProfileInput): Promise<User> {
    try {
      return await api.patch('/auth/me', input, userSchema)
    } catch (error) {
      logRequestFailure('Failed to update profile', {}, error)
      throw error
    }
  }

  /** Change the signed-in user's password (verifies the current one). */
  async changePassword(input: ChangePasswordInput): Promise<void> {
    try {
      await api.post('/auth/change-password', input, z.null())
    } catch (error) {
      logRequestFailure('Failed to change password', {}, error)
      throw error
    }
  }

  /**
   * Super-admin support mode: enter a tenant. Rotates the refresh token; the
   * new session is scoped to that tenant with org-admin powers.
   */
  async enterTenant(tenantId: string, refreshToken: string): Promise<LoginResponse> {
    try {
      return await api.post(
        '/auth/enter-tenant',
        { tenantId, refreshToken },
        loginResponseSchema,
      )
    } catch (error) {
      logRequestFailure('Failed to enter tenant', { tenantId }, error)
      throw error
    }
  }

  /** Leave tenant support mode; returns a platform-only super-admin session. */
  async exitTenant(refreshToken: string): Promise<LoginResponse> {
    try {
      return await api.post('/auth/exit-tenant', { refreshToken }, loginResponseSchema)
    } catch (error) {
      logRequestFailure('Failed to exit tenant support mode', {}, error)
      throw error
    }
  }

  async enterVenue(venueId: string, refreshToken: string): Promise<LoginResponse> {
    try {
      return await api.post('/auth/enter-venue', { venueId, refreshToken }, loginResponseSchema)
    } catch (error) {
      logRequestFailure('Failed to enter venue', { venueId }, error)
      throw error
    }
  }

  async exitVenue(refreshToken: string): Promise<LoginResponse> {
    try {
      return await api.post('/auth/exit-venue', { refreshToken }, loginResponseSchema)
    } catch (error) {
      logRequestFailure('Failed to exit venue', {}, error)
      throw error
    }
  }

  async switchableVenues(): Promise<SwitchableVenuesDto> {
    try {
      return await api.get('/auth/me/venues', switchableVenuesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load switchable venues', {}, error)
      throw error
    }
  }

  async setPrimaryVenue(venueId: string): Promise<void> {
    try {
      await api.put('/auth/me/primary-venue', { venueId }, z.null())
    } catch (error) {
      logRequestFailure('Failed to set primary venue', { venueId }, error)
      throw error
    }
  }
}

export const authService = new AuthService()
